import 'raf/polyfill';
import React from 'react';
import { englishMessages, Admin, Resource, resolveBrowserLocale } from 'admin-on-rest';
import getMuiTheme from 'material-ui/styles/getMuiTheme';
import FolderShared from 'material-ui/svg-icons/file/folder-shared';
import Face from 'material-ui/svg-icons/action/face';
import Protocol from 'material-ui/svg-icons/action/description';
import Accounting from 'material-ui/svg-icons/action/account-balance';

import { pick } from 'lodash';
import 'moment/locale/sv';

import swedishMessages from 'aor-language-swedish';
import norwegianMessages from 'aor-language-norwegian';
import danishMessages from 'aor-language-danish';
import finnishMessages from 'aor-language-finnish';
import germanMessages from 'aor-language-german';
import spanishMessages from 'aor-language-spanish';
import { Route } from 'react-router-dom';

import admin from './reducers/admin';
import { FAQList, FAQEdit, FAQCreate } from './model/faq';
import { VenueShow, VenueList, VenueEdit, VenueOpenHoursEdit, VenueCreate } from './model/venues';
import { UserList, UserEdit, UserCreate } from './model/users';
import { BookingUserList, BookingUserEdit, BookingUserCreate } from './model/bookingUsers';
import { RoleList, RoleEdit, RoleCreate } from './model/roles';
import { ProductList, ProductEdit, ProductCreate, ProductShow, SortableProductList } from './model/products';
import { ProvisionalProductList, ProvisionalProductEdit, ProvisionalProductCreate } from './model/provisionalProducts';
import { PromotedProductList, PromotedProductEdit, PromotedProductCreate } from './model/promotedProducts';
import { LockedProductList, LockedProductEdit, LockedProductCreate } from './model/lockedProducts';
import { FrozenProductList } from './model/frozenProducts';
import { AllergenList, AllergenEdit, AllergenCreate } from './model/allergens';
import { DietList, DietEdit, DietCreate } from './model/diets';
import { PhraseList, PhraseEdit, PhraseCreate } from './model/phrases';
import { SupplierList, SupplierEdit, SupplierCreate } from './model/suppliers';
import { PositionList, PositionEdit, PositionCreate } from './model/position';
import { IngredientList, IngredientEdit, IngredientCreate } from './model/ingredients';
import { RecipeList, RecipeEdit, RecipeCreate } from './model/recipes';
import { StationList, StationEdit, StationCreate } from './model/stations';
import { PriceSegmentList, PriceSegmentEdit, PriceSegmentCreate } from './model/priceSegments';
import { CategoryList, CategoryEdit, CategoryCreate } from './model/categories';
import { MarketList, MarketEdit, MarketCreate } from './model/markets';
import { BookList, BookCreate, BookEdit, BookShow } from './model/books';
import { ChapterList, ChapterCreate, ChapterEdit, ChapterShow, ChapterDelete } from './model/chapters';
import { PageList, PageCreate, PageEdit, PageShow, PagesDelete } from './model/pages';
import { QuestionList, QuestionCreate, QuestionEdit, QuestionShow, QuestionDelete } from './model/questions';
import { GenreList, GenreCreate, GenreEdit, GenreShow } from './model/genre';
import { StudentList, StudentCreate, StudentEdit, StudentShow } from './model/students';
import { PspList, PspEdit, PspCreate } from './model/psps';
import { CourseList, CourseCreate, CourseEdit, CourseShow } from './model/courses';
import { NewsList, NewsCreate, NewsEdit } from './model/news';
import { CourseCategoryList, CourseCategoryCreate, CourseCategoryEdit, CourseCategoryShow } from './model/courseCategory';
import { WebsiteList, WebsiteCreate, WebsiteEdit } from './model/websites';
import { WebsitePagesList, WebsitePagesCreate, WebsitePagesEdit } from './model/websitePages';
import { WebPromosList, WebPromosCreate, WebPromosEdit } from './model/webPromos';
import { AliasList } from './model/alias';
import { AccountingPlanList, AccountingPlanEdit, AccountingPlanCreate } from './model/accountingPlans';
import { AccountingPlanV2List, AccountingPlanV2Edit } from './model/accountingPlansV2';
import { QuizList, QuizCreate, QuizEdit } from './model/quiz';
import { AccountsList, AccountsEdit, AccountsCreate } from './model/accounts';

import AppLayout from './components/AppLayout';
import { InactiveAssortmentList, AssortmentList, AssortmentEdit } from './components/Assortment';
import FAQ from './components/FAQViewer';
import Profile from './components/Profile';
import Translation from './components/Translations/Translation';
import Organisation from './components/Organisation';
import ContactList from './components/ContactList';
import Menu from './components/Menu';
import TieredProducts from './components/TieredProducts';
import ProductActivation from './components/ProductActivation';
import LoginPage from './components/LoginPage';
import sv from './i18n/sv';
import en from './i18n/en';
import NewsViewer from './components/NewsViewer';
import TeamTailor from './components/TeamTailor';
import Games from './components/Games';
import Statistics from './components/Statistics/';
import PlotViewer from './components/PlotViewer';
// import PageBuilder from './components/PageBuilder';
import Delete from './components/Overrides/mui/delete/Delete';
import PageBuilder from './components/PageBuilder';
import Simulator from './components/Simulator';
import Diploma from './components/Diploma';
import RatingsBuilder from './components/Ratings';
import Dashboard from './components/Dashboard/index';
import Documents from './components/Documents';

import { kunal } from './client';
import kunalClient from './client/kunalClient';
import addUploadCapability from './client/addUploadCapability';
import authClient from './auth';

import theme from './utils/theme';
import PinchosGlobal from './components/PinchosGlobal';
import NotFound from './NotFound';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4042/api';

const domainMessages = {
  sv,
  en,
};
const messages = {
  en: { ...englishMessages, ...domainMessages.en },
  sv: { ...swedishMessages, ...domainMessages.sv },
  nb: { ...norwegianMessages, ...domainMessages.en },
  da: { ...danishMessages, ...domainMessages.en },
  fi: { ...finnishMessages, ...domainMessages.en },
  de: { ...germanMessages, ...domainMessages.en },
  es: { ...spanishMessages, ...domainMessages.en },
};

export const resources = [
  { name: 'vip', identifier: 'vip', hideFirstChild: true, icon: Face },
  { name: 'accounting', identifier: 'accounting', hideFirstChild: true, icon: Accounting },
  { name: 'venues', identifier: 'name', show: VenueShow, list: VenueList, edit: VenueEdit, create: VenueCreate },
  { name: 'assortments', identifier: 'name', list: AssortmentList, edit: AssortmentEdit },
  { name: 'product-activation', identifier: 'name', list: InactiveAssortmentList, edit: ProductActivation },
  { name: 'products', identifier: 'code', list: ProductList, edit: ProductEdit, create: ProductCreate, show: ProductShow },
  { name: 'product-order', identifier: 'code', parent: 'products', list: SortableProductList },
  { name: 'provisional-products', identifier: 'code', list: ProvisionalProductList, edit: ProvisionalProductEdit, create: ProvisionalProductCreate, remove: Delete },
  { name: 'promoted-products', parent: 'products', options: { label: 'Promoted products' }, list: PromotedProductList, edit: PromotedProductEdit, create: PromotedProductCreate, remove: Delete },
  { name: 'locked-products', identifier: 'code', parent: 'products', options: { label: 'Locked products' }, list: LockedProductList, edit: LockedProductEdit, create: LockedProductCreate, remove: Delete },
  { name: 'frozen-products', identifier: 'code', parent: 'products', list: FrozenProductList, remove: Delete },
  { name: 'tiered-products', resource: 'markets', parent: 'products', list: TieredProducts },
  { name: 'price-segments', resource: 'price-segments', identifier: 'name', parent: 'products', list: PriceSegmentList, edit: PriceSegmentEdit, create: PriceSegmentCreate },
  { name: 'suppliers', parent: 'products', identifier: 'name', list: SupplierList, edit: SupplierEdit, create: SupplierCreate, remove: Delete },
  { name: 'ingredients', parent: 'products', list: IngredientList, edit: IngredientEdit, create: IngredientCreate, remove: Delete },
  { name: 'recipes', parent: 'products', list: RecipeList, edit: RecipeEdit, create: RecipeCreate, remove: Delete },
  { name: 'stations', parent: 'products', identifier: 'name', list: StationList, edit: StationEdit, create: StationCreate, remove: Delete },
  { name: 'allergens', parent: 'products', identifier: 'name', list: AllergenList, edit: AllergenEdit, create: AllergenCreate, remove: Delete },
  { name: 'diets', parent: 'products', identifier: 'name', list: DietList, edit: DietEdit, create: DietCreate, remove: Delete },
  { name: 'phrases', list: PhraseList, edit: PhraseEdit, create: PhraseCreate, remove: Delete },
  { name: 'faq', list: FAQList, edit: FAQEdit, create: FAQCreate, remove: Delete },
  { name: 'categories', parent: 'products', list: CategoryList, edit: CategoryEdit, create: CategoryCreate, remove: Delete },
  { name: 'markets', parent: 'products', list: MarketList, edit: MarketEdit, create: MarketCreate, remove: Delete },
  { name: 'booking-users', identifier: 'username', parent: 'users', list: BookingUserList, edit: BookingUserEdit, create: BookingUserCreate, remove: Delete },
  { name: 'users', identifier: 'id', list: UserList, edit: UserEdit, create: UserCreate, remove: Delete },
  { name: 'positions', parent: 'users', identifier: 'identifier', list: PositionList, edit: PositionEdit, create: PositionCreate, remove: Delete },
  { name: 'roles', parent: 'users', identifier: 'id', list: RoleList, edit: RoleEdit, create: RoleCreate, remove: Delete },
  { name: 'books', list: BookList, create: BookCreate, edit: BookEdit, show: BookShow, parent: 'academy', remove: Delete },
  { name: 'chapters', list: ChapterList, create: ChapterCreate, edit: ChapterEdit, show: ChapterShow, parent: 'academy', remove: ChapterDelete },
  { name: 'pages', list: PageList, create: PageCreate, edit: PageEdit, show: PageShow, parent: 'academy', remove: PagesDelete },
  { name: 'questions', list: QuestionList, create: QuestionCreate, edit: QuestionEdit, show: QuestionShow, remove: QuestionDelete },
  { name: 'genres', list: GenreList, create: GenreCreate, edit: GenreEdit, show: GenreShow, parent: 'books', remove: Delete },
  { name: 'students', list: StudentList, identifier: 'phoneNumber', create: StudentCreate, edit: StudentEdit, show: StudentShow, parent: 'users', remove: Delete },
  { name: 'psps', hidden: true, identifier: 'name', list: PspList, edit: PspEdit, create: PspCreate },
  { name: 'courses', show: CourseShow, list: CourseList, create: CourseCreate, edit: CourseEdit, remove: Delete, parent: 'academy' },
  { name: 'news', list: NewsList, create: NewsCreate, edit: NewsEdit, remove: Delete },
  { name: 'courseCategory', list: CourseCategoryList, create: CourseCategoryCreate, edit: CourseCategoryEdit, show: CourseCategoryShow, remove: Delete, parent: 'courses' },
  { name: 'web-sites', list: WebsiteList, create: WebsiteCreate, edit: WebsiteEdit },
  { name: 'web-pages', list: WebsitePagesList, create: WebsitePagesCreate, edit: WebsitePagesEdit, remove: Delete },
  { name: 'web-promos', list: WebPromosList, create: WebPromosCreate, edit: WebPromosEdit },
  { name: 'alias', parent: 'products', list: AliasList, identifier: 'product' },
  { name: 'accounting-plans', parent: 'accounting', identifier: 'venue', list: AccountingPlanList, edit: AccountingPlanEdit, create: AccountingPlanCreate },
  { name: 'accounting-plans-v2', parent: 'accounting', identifier: 'name', list: AccountingPlanV2List, edit: AccountingPlanV2Edit },
  { name: 'egghunt', identifier: 'phoneNumber' },
  { name: 'documents', identifier: 'documents', parent: 'vip', icon: FolderShared },
  { name: 'quizes', list: QuizList, create: QuizCreate, edit: QuizEdit, remove: Delete, parent: 'vip', icon: Protocol },
  { name: 'accounts', parent: 'accounting', list: AccountsList, edit: AccountsEdit, create: AccountsCreate, remove: Delete },
];

export const restClient = kunalClient(API_BASE_URL, kunal, resources.filter((res) => res.identifier));

let user;

/* eslint-disable react/prefer-stateless-function, react/no-multi-comp */
const userHoc = (permissions, Component) => (
  class extends React.Component {
    render = () => {
      user = JSON.parse(localStorage.getItem('user'));
      return (
        <Component
          {...this.props}
          user={user}
          permissions={permissions}
        />
      );
    }
  }
);
/* eslint-enable react/prefer-stateless-function */

const authReducer = (state = { user: null, permissions: null }, { type, payload = {} }) => {
  switch (type) {
    case '@@INIT': // bind localstorage data to redux store on @@init
    case '@@redux/INIT': // prefix for development on mobile...?
    case 'AOR/USER_CHECK': {
      // console.log('hello from', type, payload.user || JSON.parse(localStorage.getItem('user')));
      return {
        ...state,
        user: payload.user || JSON.parse(localStorage.getItem('user')),
        permissions: payload.permissions || JSON.parse(localStorage.getItem('permissions')),
      };
    }
    default:
      return state;
  }
};

const App = () => (
  <Admin
    title="CATALOG"
    restClient={addUploadCapability(restClient)}
    authClient={(type, params) => authClient(type, params, API_BASE_URL)}
    appLayout={AppLayout}
    menu={Menu}
    catchAll={NotFound}
    loginPage={LoginPage}
    locale={localStorage.getItem('saved_locale') || resolveBrowserLocale('en')}
    messages={messages}
    customReducers={{ auth: authReducer, admin }}
    theme={getMuiTheme(theme)}
    dashboard={(props) => <NewsViewer resource="news" {...props} />}
    customRoutes={[
      <Route exact path="/organisation" render={(props) => <Organisation resource="users" {...props} />} />,
      <Route exact path="/pn-contact" render={(props) => <ContactList resource="users" {...props} />} />,
      <Route exact path="/faq-viewer" render={(props) => <FAQ resource="faq" {...props} />} />,
      <Route exact path="/open-hours/:id" render={(props) => <VenueOpenHoursEdit resource="venues" {...props} />} />,
      <Route exact path="/profile/:id" render={(props) => <Profile resource="users" {...props} />} />,
      <Route exact path="/team-tailor" render={(props) => <TeamTailor {...props} />} />,
      <Route exact path="/games" render={(props) => <Games {...props} />} />,
      <Route exact path="/statistics" render={(props) => <Statistics {...props} />} />,
      <Route exact path="/statistics/:id" render={(props) => <PlotViewer {...props} />} />,
      <Route exact path="/page-builder/:id" render={(props) => <PageBuilder {...props} />} />,
      <Route exact path="/profile/:id/diploma" render={(props) => <Diploma user={user} {...props} />} />,
      <Route path="/pinchos" render={(props) => <PinchosGlobal user={user} {...props} />} />,
      <Route path="/translations" render={(props) => <Translation user={user} resource="products" {...props} />} />,
      <Route path="/simulator" render={(props) => <Simulator user={user} {...props} />} />,
      <Route path="/ratings" render={(props) => <RatingsBuilder user={user} {...props} />} />,
      <Route path="/dashboard" render={(props) => <Dashboard user={user} {...props} />} />,
      <Route path="/documents" render={(props) => <Documents resource="users" user={user} {...props} />} />,
    ]}
  >
    {(permissions) => {
      user = JSON.parse(localStorage.getItem('user'));

      if (permissions && permissions.resources) {
        const resourceElements = resources.map((resource) => {
          const resourcePermissions = permissions.resources[resource.name] ? {
            ...permissions.resources[resource.name],
            ...pick(permissions, [
              'name',
              'subRoles',
              'priceEditor',
              'priceManager',
              'translator',
            ]),
            superUser: !!user.superUser,
          } : null;

          const { create, list, edit, remove, show, ...props } = resource;
          return (
            resourcePermissions && resourcePermissions.list
              ? (
                <Resource
                  {...props}
                  hidden={resourcePermissions.hidden}
                  show={resourcePermissions.show ? userHoc(resourcePermissions, show) : null}
                  create={resourcePermissions.create ? userHoc(resourcePermissions, create) : null}
                  list={resourcePermissions.list ? userHoc(resourcePermissions, list) : null}
                  edit={resourcePermissions.edit ? userHoc(resourcePermissions, edit) : null}
                  remove={resourcePermissions.delete ? remove : null}
                />
              )
              : null
          );
        });
        return (
          resourceElements
        );
      }
      return null;
    }}
  </Admin>
);

export default App;
